// Transport DTOs for Attendance Marks endpoint

import type { EmployeeFilteredStatus } from '@/api/domains/employee/types';


export type AttendanceMarkStatus = 0 | 1;

// Employee status filter, distinct from AttendanceMarkStatus (the mark's enabled/disabled state).
// Reuses the canonical type owned by the employee dimensional-filter domain (same 'vigente' |
// 'no_vigente' | 'todos' values consumed by the filter panel) instead of duplicating it.
export type EmployeeStatusFilter = EmployeeFilteredStatus;

// Stable backend marker for the enabled status. Used by the mapper to derive
// the UI flag `isEnabled` without coupling the UI to translated strings.
export const BACKEND_MARK_STATUS_ENABLED = 'Habilitado' as const;

// Hybrid Export / Async Job result for POST /attendance/marks/export: the backend responds 200
// with the file when the volume is under the sync threshold, or 202 with a jobId when it exceeds
// it — the job is processed in the background and delivered via email / the async jobs tray.
export type AttendanceMarksExportHybridResult =
  | { type: 'sync'; blob: Blob; filename: string }
  | { type: 'async'; jobId: string };

// Dimensional filters shared by the marks search and export requests — mirrors the backend's
// LC_DimensionalFiltersInWithPagination base. CSV fields (e.g. "1,2,3"); null/omitted = no filter.
interface LC_DimensionalFiltersIn {
  companyIds?: string | null;
  areaIds?: string | null;
  positionIds?: string | null;
  costCenterIds?: string | null;
  locationIds?: string | null;
  contractModalityIds?: string | null;
  groupIds?: string | null;
  employeeStatus?: EmployeeStatusFilter | null; // default 'vigente' when omitted
}

// Request DTO — employeeIds and eventIds are CSV strings (e.g. "1,2,3").
// employeeIds takes precedence over dimensional filters when resolving the employee universe.
export interface LC_GetAttendanceMarksIn extends LC_DimensionalFiltersIn {
  employeeIds?: string | null;
  eventIds?: string | null;
  dateFrom?: string | null; // YYYY-MM-DD
  dateTo?: string | null;   // YYYY-MM-DD
  status?: AttendanceMarkStatus | null;
  page?: number;
  pageSize?: number;
  searchText?: string | null;
  orderBy?: string | null;
  orderDirection?: 'ASC' | 'DESC' | null;
}

// Export request DTO — same filters as the search, without pagination.
export interface LC_ExportAttendanceMarksIn extends LC_DimensionalFiltersIn {
  employeeIds?: string | null;
  eventIds?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  status?: AttendanceMarkStatus | null;
}

// Response item DTO
export interface LC_AttendanceMarkItem {
  historicalAttendanceId: number;
  fullName: string;
  employeeTaxIdentificationNumber: string;
  eventName: string;
  ubicationName: string;
  markDate: string;   // dd/MM/yyyy
  markTime: string;   // HH:mm:ss
  markStatus: string; // "Habilitado" | "Deshabilitado"
  modifiedDate: string;
  modifiedByName: string;
}

// Pagination metadata
interface LC_AttendanceMarksPagination {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  searchText?: string | null;
  orderBy?: string | null;
  orderDirection?: string | null;
}

// Response DTO
export interface LC_GetAttendanceMarksOut {
  items: LC_AttendanceMarkItem[];
  pagination: LC_AttendanceMarksPagination;
}

// Response DTO for PATCH toggle-enabled — message is the SP-provided text (or a
// system-generated fallback), which may communicate that the change is pending
// approval instead of confirming an immediate state change.
export interface LC_ToggleMarkEnabledOut {
  message: string;
}

// Internal UI model
export interface AttendanceMark {
  id: number;
  fullName: string;
  rut: string;
  eventName: string;
  locationName: string;
  date: string;  // dd/MM/yyyy (display)
  time: string;  // HH:mm:ss
  status: string;
  isEnabled: boolean;
  modifiedDate: string;
  modifiedByName: string;
}

export interface AttendanceMarksPage {
  items: AttendanceMark[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
}
